import { IAuthTokenResultDto, IAuthAccount, IJwtAuthTokenResult } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import rabbit = require("crypto-js/rabbit");
import utf8 = require("crypto-js/enc-utf8");
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc' // import plugin
import { logger } from "./logger";
import { axiosRetryPost } from "./AxiosRetryUtil";
import generalModule from "./GeneralModule";
import { shared } from "../src/taskpane/config/constants";
import { StorageHelper } from "./StorageHelper";
import { ITokenRequest, ITokenResponse } from "../src/taskpane/components/_v2/redux/api/nintex/api-nintex-interface";
import { apiAquireFlowToken, apiAquireMailSendToken, apiAquireMailboxToken, apiAquireSharePointAdminToken, apiCheckIsTessAuthenticated, apiRefreshTokenRequest, apiSilentTokenRequest, refreshJwt } from "../src/taskpane/services/ApiService";
import { clearLocalStorage } from "../src/taskpane/components/helpers/localStorageHelper";

dayjs.extend(utc);

enum TokenStorage {
    Local,
    Roaming
}

export enum TokenResource {
    All,
    Graph,
    SharePoint,
    OneDrive,
    Flow,
    SharePointAdmin,
    MailSend,
    Nintex,
    Mailbox,
    Teams,
    Tess
}

export enum DialogMessageStatus {
    Success,
    Failure
}

export interface DialogMessageResponse {
    status: DialogMessageStatus;
    result: IAuthTokenResultDto | null;
}

export class AuthModule {

    private static readonly CypherSecret: string = "xbl001=klr";
    private static readonly defaultRefreshWait: number = 2000;
    private static readonly defaultRefreshWaitDepth: number = 5;

    private static AuthStateKey: string = "AuthState";

    private isAuthRefreshInProgress: boolean = false;
    private isFlowAccessTokenRefreshInProgress: boolean = false;
    private isMailSendAccessTokenRefreshInProgress: boolean = false;
    private isMailboxAccessTokenRefreshInProgress: boolean = false;
    private globalAuthState: IAuthTokenResultDto;
    private tenantApiKey: string;

    // Serializes roaming saveAsync calls so concurrent persists (multiple token getters refreshing
    // on load) never collide with Outlook's simultaneous-async-call limit, which is the most likely
    // cause of saveAsync failures in new Outlook / Outlook on the web.
    private roamingSaveChain: Promise<unknown> = Promise.resolve();
    // Last logged roaming-failure signature, so a repeating failure is reported once, not every refresh.
    private lastRoamingErrorSignature: string = null;

    private signOutCallback: () => void;

    setSignOutCallback(callback: () => void) {
        this.signOutCallback = callback;
    }

    private async tryPersistAuthState(storage: TokenStorage, authState: IAuthTokenResultDto): Promise<boolean> {
        try {
            if (storage === TokenStorage.Roaming) {
                if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                    try {
                        Office.context.roamingSettings.set(AuthModule.AuthStateKey, authState);
                    } catch (setError) {
                        // e.g. error 9057 when the serialized auth state exceeds the 32 KB roaming limit.
                        this.logRoamingSaveFailureOnce(setError?.code, setError?.message);
                        return false;
                    }

                    return await this.queueRoamingSave();
                }
            }
            else if (storage === TokenStorage.Local) {
                const storageValue: string = rabbit.encrypt(JSON.stringify(authState), AuthModule.CypherSecret).toString();
                try {
                    localStorage.setItem(AuthModule.AuthStateKey, storageValue);
                } catch (error) {
                    return await StorageHelper.setItemAsync(AuthModule.AuthStateKey, storageValue);
                }
                return true;
            }

        } catch (error) {
            logger(error, "AuthModule.ts (tryPersistAuthState)");
        }

        return false;
    }

    // Runs one roamingSettings.saveAsync at a time. Chaining the saves prevents several concurrent
    // persists (e.g. graph/flow/mailSend tokens all refreshing on load) from issuing simultaneous
    // async Office calls, which new Outlook / Outlook on the web reject once too many are in flight.
    private queueRoamingSave(): Promise<boolean> {
        // Chain on the previous save (which never rejects - see the trailing catch) so at most one
        // saveAsync is ever in flight. The trailing catch also guarantees this promise never rejects,
        // so a synchronous throw from saveAsync can't poison the chain or escape un-deduped.
        const attempt: Promise<boolean> = this.roamingSaveChain
            .then(() => new Promise<Office.AsyncResult<void>>(resolve =>
                Office.context.roamingSettings.saveAsync(resolve)))
            .then(result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    this.lastRoamingErrorSignature = null; // a fresh failure episode should be logged again
                    return true;
                }
                this.logRoamingSaveFailureOnce(result.error?.code, result.error?.message);
                return false;
            })
            .catch(error => {
                this.logRoamingSaveFailureOnce(error?.code, error?.message);
                return false;
            });

        this.roamingSaveChain = attempt;
        return attempt;
    }

    // Logs a roaming persistence failure at most once per distinct error, so a persistent failure
    // (which recurs on every token refresh) doesn't flood the logs. Surfaces the real code/message
    // instead of the boolean that previously reached the logger as the string "undefined".
    private logRoamingSaveFailureOnce(code: number | undefined, message: string | undefined) {
        const signature: string = `${code ?? "?"}:${message ?? ""}`;
        if (this.lastRoamingErrorSignature === signature) {
            return;
        }
        this.lastRoamingErrorSignature = signature;
        logger(new Error(`Roaming AuthState persistence failed (code ${code ?? "unknown"}): ${message ?? "unknown"}`), "AuthModule.ts (tryPersistAuthState)");
    }

    async persistAuthState(authState: IAuthTokenResultDto) {
        this.globalAuthState = authState;
        // Local storage (plus the in-memory globalAuthState above) is the authoritative read path for
        // this device, so it must complete before we return. Roaming persistence is a best-effort,
        // cross-device convenience that round-trips to Exchange - it must never block the auth flow or
        // delay a token refresh, so it is fired without awaiting.
        void this.tryPersistAuthState(TokenStorage.Roaming, authState);
        await this.tryPersistAuthState(TokenStorage.Local, authState);
    }

    private tryGetAuthState(storage: TokenStorage): IAuthTokenResultDto {
        try {

            if (storage === TokenStorage.Roaming) {
                if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                    return Office.context.roamingSettings.get(AuthModule.AuthStateKey);
                }
            } else if (storage === TokenStorage.Local) {
                let serializedAuthState: string;
                try {
                    serializedAuthState = localStorage.getItem(AuthModule.AuthStateKey);
                } catch (error) {
                    serializedAuthState = StorageHelper.getItem(AuthModule.AuthStateKey);
                }

                if (serializedAuthState) {
                    return JSON.parse(rabbit.decrypt(serializedAuthState, AuthModule.CypherSecret).toString(utf8));
                }
            }

        } catch (error) {
            logger(error, "AuthModule.ts (tryGetAuthState)");
        }

        return null;
    }

    getPersistedAuthState(): IAuthTokenResultDto {

        let authState: IAuthTokenResultDto = null;

        if (this.globalAuthState) {
            return this.globalAuthState;
        }
        else {
            try {
                const roamingAuthState: IAuthTokenResultDto = this.tryGetAuthState(TokenStorage.Roaming);
                const localAuthState: IAuthTokenResultDto = this.tryGetAuthState(TokenStorage.Local);
                if (roamingAuthState && localAuthState) {
                    const roamingExpiration = +dayjs.utc(roamingAuthState.graphTokenExpiresOn);
                    const localExpiration = +dayjs.utc(localAuthState.graphTokenExpiresOn);
                    if (roamingExpiration === localExpiration) {
                        authState = roamingAuthState;
                    }
                    else if (localExpiration > roamingExpiration) {
                        authState = localAuthState;
                        this.tryPersistAuthState(TokenStorage.Roaming, authState);
                    }
                    else {
                        authState = roamingAuthState;
                        this.tryPersistAuthState(TokenStorage.Local, authState);
                    }
                }
                else if (roamingAuthState && !localAuthState) {
                    authState = roamingAuthState;
                    this.tryPersistAuthState(TokenStorage.Local, authState);
                } else if (localAuthState && !roamingAuthState) {
                    authState = localAuthState;
                    this.tryPersistAuthState(TokenStorage.Roaming, authState);
                }

                if (authState && authState.account) {
                    this.globalAuthState = authState;
                }
                else {
                    authState = null;
                }
            } catch (error) {
                logger(error, "AuthModule.ts (getPersistedAuthState)");
            }
        }

        return authState;
    }

    clearPersistedAuthState() {
        try {
            if (this.globalAuthState) {
                this.globalAuthState = undefined;
            }

            if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                Office.context.roamingSettings.remove(AuthModule.AuthStateKey);

                Office.context.roamingSettings.saveAsync(function (result) {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.error(`RoamingSettings SaveAsync failed with message: ${result.error.message}`);
                    }
                });
            }

            localStorage.removeItem(AuthModule.AuthStateKey);
            clearLocalStorage();
        } catch (error) {
            logger(error, "AuthModule.ts (clearPersistedAuthState)");
        }
    }

    private calculateMinutesToExpiration(tokenExpirationDate: Date): number {
        if (tokenExpirationDate) {
            try {
                const utcNow = dayjs.utc();
                const tokenExpirationUtc = dayjs.utc(tokenExpirationDate);
                const minutesToExpiration = tokenExpirationUtc.diff(utcNow, "minutes");
                return minutesToExpiration;
            } catch (error) {
                logger(error, "AuthModule.ts (calculateMinutesToExpiration)");
            }
        }
        return 0;
    }

    private getTokenExpirationDate(resource: TokenResource, authState: IAuthTokenResultDto): Date {
        let tokenExpirationDate: Date;

        if (authState) {
            try {
                switch (resource as TokenResource) {
                    case TokenResource.All:

                        const expirationUnixTimeValues: number[] = [];

                        if (authState.graphTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.graphTokenExpiresOn));
                        if (authState.sharePointTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.sharePointTokenExpiresOn));
                        if (authState.oneDriveTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.oneDriveTokenExpiresOn));
                        // if (authState.informationProtectionTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.informationProtectionTokenExpiresOn));
                        if (authState.teamsTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.teamsTokenExpiresOn));

                        if (expirationUnixTimeValues.length > 0) {
                            tokenExpirationDate = new Date(Math.min(...expirationUnixTimeValues));
                        }

                        break;
                    case TokenResource.Graph:
                        tokenExpirationDate = authState.graphTokenExpiresOn;
                        break;
                    case TokenResource.SharePoint:
                        tokenExpirationDate = authState.sharePointTokenExpiresOn;
                        break;
                    case TokenResource.OneDrive:
                        tokenExpirationDate = authState.oneDriveTokenExpiresOn;
                        break;
                    case TokenResource.Flow:
                        tokenExpirationDate = authState.flowTokenExpiresOn;
                        break;
                    case TokenResource.SharePointAdmin:
                        tokenExpirationDate = authState.sharePointAdminTokenExpiresOn;
                        break;
                    case TokenResource.MailSend:
                        tokenExpirationDate = authState.mailSendTokenExpiresOn;
                        break;
                    // case TokenResource.Mailbox:
                    //     tokenExpirationDate = authState.mailboxTokenExpiresOn;
                    //     break;
                    case TokenResource.Teams:
                        tokenExpirationDate = authState.teamsTokenExpiresOn;
                        break;
                }
            } catch (error) {
                logger(error, "AuthModule.ts (getTokenExpirationDate)");
            }
        }

        return tokenExpirationDate;
    }

    private tokenRefreshRequired(tokenExpirationDate: Date): boolean {
        if (tokenExpirationDate) {
            const minutesToExpiration = this.calculateMinutesToExpiration(tokenExpirationDate);
            if (minutesToExpiration > 2) {
                return false;
            }
            return true;
        }
        return false;
    }

    private getIsResourceRefreshInProgress(resource: TokenResource): boolean {
        if (resource === TokenResource.Flow) {
            return this.isFlowAccessTokenRefreshInProgress === true;
        }

        if (resource === TokenResource.MailSend) {
            return this.isMailSendAccessTokenRefreshInProgress === true;
        }

        if (resource === TokenResource.Mailbox) {
            return this.isMailboxAccessTokenRefreshInProgress === true;
        }

        return this.isAuthRefreshInProgress === true;
    }

    private async waitOnRefresh(resource: TokenResource, depth: number = AuthModule.defaultRefreshWaitDepth): Promise<void> {
        if (this.getIsResourceRefreshInProgress(resource) === true && depth > 0) {
            await new Promise<void>(resolve => setTimeout(async () => resolve(await this.waitOnRefresh(resource, --depth)), AuthModule.defaultRefreshWait));
        }
    }

    private async getAuthState(resource: TokenResource): Promise<IAuthTokenResultDto> {
        if (this.isAuthRefreshInProgress === true) await this.waitOnRefresh(resource);
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState) {
            if (this.tokenRefreshRequired(this.getTokenExpirationDate(resource, authState))) {
                this.isAuthRefreshInProgress = true;
                if (authState.account.sharePointBaseUrl) {
                    authState = await this.requestTokenRefresh(authState.account);
                }
                else {
                    authState = await this.requestTokenSilent(authState.account.accountId);
                }
                this.isAuthRefreshInProgress = false;
            }
        }
        return authState;
    }

    requestTokenSilent = async (accountId: string): Promise<IAuthTokenResultDto> => {
        try {
            let refreshedAuthState = await apiSilentTokenRequest(accountId);
            return await this.configureAndPersistRefreshedAuthState(refreshedAuthState);
        } catch (error) {
            logger(error, "AuthModule.ts (requestTokenSilent)");

            this.clearPersistedAuthState();
            return null;
        }
    }

    requestTokenRefresh = async (account: IAuthAccount): Promise<IAuthTokenResultDto> => {
        try {
            let refreshedAuthState = await apiRefreshTokenRequest(account);
            return await this.configureAndPersistRefreshedAuthState(refreshedAuthState);
        } catch (error) {
            logger(error, "AuthModule.ts (requestTokenRefresh)");

            this.clearPersistedAuthState();
            return null;
        }
    }

    private async configureAndPersistRefreshedAuthState(refreshedAuthState: IAuthTokenResultDto): Promise<IAuthTokenResultDto> {
        if (refreshedAuthState) {
            const persistedAuthState: IAuthTokenResultDto = this.getPersistedAuthState();
            refreshedAuthState.flowToken = persistedAuthState?.flowToken;
            refreshedAuthState.flowTokenExpiresOn = persistedAuthState?.flowTokenExpiresOn;
            refreshedAuthState.sharePointAdminToken = persistedAuthState?.sharePointAdminToken;
            refreshedAuthState.sharePointAdminTokenExpiresOn = persistedAuthState?.sharePointAdminTokenExpiresOn;
            // refreshedAuthState.mailSendToken = persistedAuthState?.mailSendToken;
            // refreshedAuthState.mailSendTokenExpiresOn = persistedAuthState?.mailSendTokenExpiresOn;
            // refreshedAuthState.mailboxToken = persistedAuthState?.mailboxToken;
            // refreshedAuthState.mailboxTokenExpiresOn = persistedAuthState?.mailboxTokenExpiresOn;

            await this.persistAuthState(refreshedAuthState);
        }
        return refreshedAuthState;
    }

    async signIn(): Promise<IAuthTokenResultDto | null> {
        // return await this.requestTokenSilent("7e9992b2-a3bf-4019-a27d-033d00ce6598.3f50a620-6f6b-4a76-a8fe-57fce2ef9e1d");
        return await this.getAuthState(TokenResource.All);
    }

    async signOut(): Promise<void> {
        this.clearPersistedAuthState();
        this.signOutCallback();
    }

    async getGraphAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.Graph);
        if (authState) {
            return authState.graphToken;
        }
        else {
            this.signOutCallback();
            return null;
        }
    }

    async getTeamsAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.Teams);
        if (authState) {
            return authState.teamsToken;
        }
        else {
            this.signOutCallback();
            return null;
        }
    }

    async getSharePointAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.SharePoint);
        if (authState) {
            return authState.sharePointToken;
        }
        else {
            this.signOutCallback();
            return null;
        }
    }

    async getOneDriveAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.OneDrive);
        if (authState) {
            return authState.oneDriveToken;
        }
        else {
            this.signOutCallback();
            return null;
        }
    }

    async getFlowAccessToken(forceRefresh?: boolean): Promise<string> {
        if (this.isFlowAccessTokenRefreshInProgress === true) await this.waitOnRefresh(TokenResource.Flow);
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState) {
            if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Flow, authState))) {
                this.isFlowAccessTokenRefreshInProgress = true;
                try {
                    const flowTokenResponse = await apiAquireFlowToken(authState);
                    if (flowTokenResponse?.flowToken) {
                        authState.flowToken = flowTokenResponse?.flowToken;
                        authState.flowTokenExpiresOn = flowTokenResponse?.flowTokenExpiresOn;
                        this.persistAuthState(authState);
                    }
                } catch (error) {
                    logger(error, "AuthModule.ts (getFlowAccessToken)");
                }
                this.isFlowAccessTokenRefreshInProgress = false;
            }
        }
        return authState?.flowToken;
    }

    async getSharePointAdminAccessToken(forceRefresh?: boolean): Promise<string> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState) {
            if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.SharePointAdmin, authState))) {
                try {
                    const sharePointAdminTokenResponse: IAuthTokenResultDto = await apiAquireSharePointAdminToken(authState);
                    if (sharePointAdminTokenResponse?.sharePointAdminToken) {
                        authState.sharePointAdminToken = sharePointAdminTokenResponse?.sharePointAdminToken;
                        authState.sharePointAdminTokenExpiresOn = sharePointAdminTokenResponse?.sharePointAdminTokenExpiresOn;
                        this.persistAuthState(authState);
                    }
                } catch (error) {
                    logger(error, "AuthModule.ts (getSharePointAdminAccessToken)");
                }
            }
        }
        return authState?.sharePointAdminToken;
    }

    async getMailSendAccessToken(forceRefresh?: boolean): Promise<string> {
        if (this.isMailSendAccessTokenRefreshInProgress === true) await this.waitOnRefresh(TokenResource.MailSend);
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState) {
            // A missing token (or a token without an expiry) must count as "refresh required":
            // tokenRefreshRequired(undefined) is false, which previously turned an absent token into
            // a silent `undefined` return and a "Bearer undefined" Graph call.
            const mailSendTokenMissing: boolean = !authState.mailSendToken || !authState.mailSendTokenExpiresOn;
            if (forceRefresh || mailSendTokenMissing || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.MailSend, authState))) {
                this.isMailSendAccessTokenRefreshInProgress = true;
                try {
                    const mailSendTokenResponse: IAuthTokenResultDto = await apiAquireMailSendToken(authState);
                    if (mailSendTokenResponse?.mailSendToken) {
                        // Merge into whatever is persisted NOW, not the snapshot taken before the await:
                        // a Graph/SharePoint refresh may have replaced the state in the meantime, and
                        // persisting the stale snapshot would clobber those fresh tokens.
                        authState = this.getPersistedAuthState() ?? authState;
                        authState.mailSendToken = mailSendTokenResponse.mailSendToken;
                        authState.mailSendTokenExpiresOn = mailSendTokenResponse.mailSendTokenExpiresOn;
                        await this.persistAuthState(authState);
                    }
                } catch (error) {
                    logger(error, "AuthModule.ts (getMailSendAccessToken)");
                }
                this.isMailSendAccessTokenRefreshInProgress = false;
            }
        }
        return authState?.mailSendToken || undefined;
    }

    async getDocubirdToken(refreshRequired?: boolean): Promise<string> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState?.docubirdToken) {
            if (refreshRequired === true || this.tokenRefreshRequired(new Date(authState.docubirdToken.expirationDate))) {
                try {
                    const jwtAuthTokenReponse: IJwtAuthTokenResult = await refreshJwt(authState.docubirdToken.token);

                    if (!jwtAuthTokenReponse) return null;

                    authState.docubirdToken = jwtAuthTokenReponse;
                    this.persistAuthState(authState);
                } catch (error) {
                    logger(error, "AuthModule.ts (getDocubirdToken)");
                }
            }
        }
        return authState?.docubirdToken?.token;
    }

    getDocubirdTokenCached(): string {
        return this.getPersistedAuthState()?.docubirdToken?.token;
    }

    async getNintexAccessToken(client_id: string, client_secret: string, forceRefresh: boolean = false): Promise<string> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();

        if (authState) {
            if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Nintex, authState))) {
                try {
                    let authUrl: URL = new URL(`https://eu.nintex.io/authentication/v1/token`);

                    const body: ITokenRequest = {
                        client_id,
                        client_secret,
                        grant_type: "client_credentials"
                    }

                    const nintexTokenResponse: ITokenResponse = (await axiosRetryPost(authUrl.href, body, { headers: { 'Content-Type': 'application/json' } }))?.data;

                    if (nintexTokenResponse?.access_token) {
                        authState.nintexToken = nintexTokenResponse?.access_token;
                        authState.nintexTokenExpiresOn = new Date(nintexTokenResponse?.expires_at);
                        this.persistAuthState(authState);
                    }
                } catch (error) {
                    logger(error, "AuthModule.ts (getMailSendAccessToken)");
                }
            }
        }
        return authState?.nintexToken;
    }

    async getIsTessAuthenticated(forceRefresh: boolean = false): Promise<boolean> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (forceRefresh) {
            try {
                const isTessAuthenticated: boolean = await apiCheckIsTessAuthenticated(authState);
                authState.isTessAuthenticated = isTessAuthenticated;
                this.persistAuthState(authState);
            } catch (error) {
                logger(error, "AuthModule.ts (getIsTessAuthenticated)");
            }
        }
        return authState?.isTessAuthenticated;
    }

    // async getMailboxAccessToken(forceRefresh?: boolean): Promise<string> {
    //     if (this.isMailboxAccessTokenRefreshInProgress === true) await this.waitOnRefresh(TokenResource.Mailbox);
    //     let authState: IAuthTokenResultDto = this.getPersistedAuthState();
    //     if (authState && authState.account?.accountId) {
    //         if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Mailbox, authState))) {
    //             this.isMailboxAccessTokenRefreshInProgress = true;
    //             try {
    //                 const mailboxTokenResponse: IAuthTokenResultDto = await apiAquireMailboxToken(authState);
    //                 if (mailboxTokenResponse?.mailboxToken && mailboxTokenResponse?.mailboxUsername) {
    //                     authState.mailboxToken = mailboxTokenResponse?.mailboxToken;
    //                     authState.mailboxTokenExpiresOn = mailboxTokenResponse?.mailboxTokenExpiresOn;
    //                     authState.mailboxAccountId = mailboxTokenResponse?.mailboxAccountId;
    //                     authState.mailboxUsername = mailboxTokenResponse?.mailboxUsername;
    //                     this.persistAuthState(authState);
    //                 }
    //             } catch (error) {
    //                 logger(error, "AuthModule.ts (getMailboxAccessToken)");
    //             }
    //             this.isMailboxAccessTokenRefreshInProgress = false;
    //         }
    //     }
    //     return authState?.mailboxToken;
    // }

    async getAccount(refreshTokensIfRequired: boolean = true): Promise<IAuthAccount> {
        const authState: IAuthTokenResultDto = refreshTokensIfRequired ? await this.getAuthState(TokenResource.All) : this.getPersistedAuthState();
        return authState?.account;
    }

    async getAuthData(): Promise<IAuthTokenResultDto | null> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.All);
        if (authState) {
            return authState;
        }
        else {
            this.signOutCallback();
            return null;
        }
    }

    getMinutesToExpiration(resource: TokenResource): number {
        return this.calculateMinutesToExpiration(this.getTokenExpirationDate(resource, this.getPersistedAuthState()));
    }

    async initiateDialogAuthFlow(loginCallback: (authResult: IAuthTokenResultDto) => Promise<void>, resource?: TokenResource, useContext?: boolean): Promise<void> {
        let loginDialog: Office.Dialog;

        const processLoginMessage = (args: { message: string; origin: string; }) => {
            const messageFromDialog: DialogMessageResponse = JSON.parse(args.message);
            if (messageFromDialog.status === DialogMessageStatus.Success) {
                if (resource === TokenResource.Flow) {
                    if (messageFromDialog.result?.flowToken) {
                        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                        if (authState) {
                            authState.flowToken = messageFromDialog.result.flowToken;
                            authState.flowTokenExpiresOn = messageFromDialog.result.flowTokenExpiresOn;
                            this.persistAuthState(authState);
                        }
                    }
                }
                // else if (resource === TokenResource.Mailbox) {
                //     if (messageFromDialog.result?.mailboxToken && messageFromDialog.result?.mailboxUsername) {
                //         let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                //         if (authState) {
                //             authState.mailboxToken = messageFromDialog.result.mailboxToken;
                //             authState.mailboxTokenExpiresOn = messageFromDialog.result.mailboxTokenExpiresOn;
                //             authState.mailboxAccountId = messageFromDialog.result.mailboxAccountId;
                //             authState.mailboxUsername = messageFromDialog.result.mailboxUsername;
                //             this.persistAuthState(authState);
                //         }
                //     }
                // }
                else if (resource === TokenResource.Tess) {
                    let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                    if (authState) {
                        authState.isTessAuthenticated = messageFromDialog.result.isTessAuthenticated;
                        this.persistAuthState(authState);
                    }
                }
                else {
                    this.persistAuthState(messageFromDialog.result);
                }
                loginCallback(messageFromDialog.result);
                loginDialog.close();
            }
            else {
                loginCallback(null);
                loginDialog.close();
            }
        };

        const processLoginDialogEvent = (arg) => {
            this.processDialogEvent(arg, loginCallback);
            loginDialog.close();
        };

        return new Promise((resolve, reject) => {
            let dialogLoginUrl: URL = new URL(process.env.baseUri + "/auth.html");

            const isInContentManagerMode: boolean = generalModule.isInConentMangerMode();
            if (isInContentManagerMode) {
                dialogLoginUrl.searchParams.append(shared.params.app, "content_manager");
            }

            if (resource) {
                dialogLoginUrl.searchParams.append(shared.params.resource, resource.toString());

                const accountDetails: IAuthAccount = this.getPersistedAuthState()?.account;
                dialogLoginUrl.searchParams.append(shared.params.username, accountDetails?.username);
                dialogLoginUrl.searchParams.append(shared.params.tenantId, accountDetails?.tenantId);
                dialogLoginUrl.searchParams.append(shared.params.accountId, accountDetails?.uniqueId);
            }
            else if (!isInContentManagerMode
                && generalModule.getIsOfficeJSAvailable()
                && Office.context.requirements.isSetSupported("Mailbox", "1.5")
                && Office.context.mailbox?.userProfile?.emailAddress) {
                dialogLoginUrl.searchParams.append(shared.params.username, Office.context.mailbox.userProfile.emailAddress);
            }

            Office.context.ui.displayDialogAsync(
                dialogLoginUrl.href,
                { height: 50, width: 35, promptBeforeOpen: false },
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.warn("Office.AsyncResultStatus.Failed: " + `${result.error.code} ${result.error.message}`);
                        reject(result.error)
                    }
                    else {
                        loginDialog = result.value;
                        loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processLoginMessage);
                        loginDialog.addEventHandler(Office.EventType.DialogEventReceived, processLoginDialogEvent);
                        resolve();
                    }
                }
            );
        });
    }

    private processDialogEvent(arg: { error: number, type: string }, callback: (authResult: IAuthTokenResultDto) => Promise<void>): void {

        switch (arg.error) {
            case 12002:
                // displayError('The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.');
                break;
            case 12003:
                // displayError('The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.');
                break;
            case 12006:
                // 12006 means that the user closed the dialog instead of waiting for it to close.
                // It is not known if the user completed the login or logout, so assume the user is
                // logged out and revert to the app's starting state. It does no harm for a user to
                // press the login button again even if the user is logged in.

                callback(null);

                break;
            default:
                // displayError('Unknown error in dialog box.');
                break;
        }
    };

    public setTenantApiKey(apiKey: string) {
        this.tenantApiKey = apiKey;
    }

    public getTenantApiKey() {
        return this.tenantApiKey;
    }

    public getCypherSecret() {
        return AuthModule.CypherSecret;
    }

    public registerMailboxAddress(address: string) {
        if (address?.trim()) {
            AuthModule.AuthStateKey = `${AuthModule.AuthStateKey}.${address}`;
        }
    }
}

const authModule = new AuthModule();
export default authModule;